/**
 * @voltxampere/web — Audio state binding (Sprint 31-6)
 *
 * Bridges the fx layer's per-component audio hints (Speaker frequency
 * = f(V_pin), Buzzer 2kHz on/off) to the @voltxampere/audio Tone.js
 * engine. Lives in apps/web (not in @voltxampere/audio itself) so the
 * audio package stays engine-independent — it only consumes
 * `AudioFrame` shapes that this module derives from the engine
 * transient sample + circuit topology.
 *
 * Three-layer audio guard (S31-6 closure):
 *   1. User AYARLAR > Ses > "Sessiz" toggle on → mute
 *   2. AYARLAR master volume slider 0 → silent
 *   3. `prefers-reduced-motion: reduce` matchMedia → suppressed (no oscillator)
 *   4. AudioContext not yet user-initialised → suppressed (lazy-init pending)
 *   5. document.visibilityState === "hidden" → silence (page tab switch)
 *
 * The fifth guard is set up in main.ts boot path; the others surface
 * here as `audioMuted`, `audioMasterVolume`, and `prefersReducedMotion`
 * signals so the AYARLAR UI + boot wiring can flip them independently.
 *
 * Lazy AudioContext init: the engine is constructed on the FIRST user
 * gesture that produces sound (Run/F5/AYARLAR slider drag). Until then
 * the suppressed guard keeps the Tone.js wrapper silent.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { computed, signal, type ReadonlySignal } from "@preact/signals-core";
import {
  buzzerActive,
  buzzerFrequency,
  buzzerVolume,
  ensureAudioContext,
  isAudioContextReady,
  speakerActive,
  speakerFrequency,
  speakerVolume,
  type AudioFrame,
  type ComponentAudioState,
  type AudioEngineHandle,
} from "@voltxampere/audio";
import type { Circuit, ComponentInstance } from "@voltxampere/schema";

import { currentCircuit } from "./circuit.js";
import { currentTransientResult, type TransientWireSample } from "./transient-runner.js";
import { prefersReducedMotion } from "./fx-state.js";

/** AYARLAR > Ses > Sessiz toggle. Default false. */
export const audioMuted = signal<boolean>(false);

/** AYARLAR > Ses > Master volume slider 0..1. Default 0.5. */
export const audioMasterVolume = signal<number>(0.5);

/**
 * Sprint 31-6 — first-run autoplay info chip dismissed flag. False
 * until user clicks the dismiss action (or AudioContext init succeeds
 * on first gesture). Persisted to settings DB v2→v3.
 */
export const audioAutoplayHinted = signal<boolean>(false);

/**
 * Page visibility tracking. `document.visibilityState === "hidden"`
 * (tab switch, window minimised) → silence. Mirrors matchMedia signal
 * pattern from fx-state.ts. main.ts boot binds the actual listener.
 */
export const documentHidden = signal<boolean>(false);

let visibilityBound = false;
export function bindVisibilityListener(): void {
  if (visibilityBound) return;
  visibilityBound = true;
  if (typeof document === "undefined") return;
  documentHidden.value = document.visibilityState === "hidden";
  document.addEventListener("visibilitychange", () => {
    documentHidden.value = document.visibilityState === "hidden";
  });
}

const EMPTY_AUDIO_FRAME: AudioFrame = { t: 0, components: new Map() };

/**
 * Derived audio state. Recomputes whenever the user's last transient
 * sample, the active circuit, or any audio guard flips. Returns an
 * empty frame when ANY guard tetiklenir or no transient is available.
 *
 * Reduced-motion DOES gate audio — repetitive tones can trigger
 * vestibular discomfort, so respecting the OS preference here matches
 * the same accessibility intent as the particle animation guard.
 */
export const currentAudioFrame: ReadonlySignal<AudioFrame> = computed(() => {
  if (audioMuted.value) return EMPTY_AUDIO_FRAME;
  if (audioMasterVolume.value <= 0) return EMPTY_AUDIO_FRAME;
  if (prefersReducedMotion.value) return EMPTY_AUDIO_FRAME;
  if (documentHidden.value) return EMPTY_AUDIO_FRAME;
  const circuit = currentCircuit.value;
  const result = currentTransientResult.value;
  if (!circuit || !result || result.timeSeries.length === 0) return EMPTY_AUDIO_FRAME;
  const lastSample = result.timeSeries[result.timeSeries.length - 1];
  if (!lastSample) return EMPTY_AUDIO_FRAME;
  return extractAudioFrame(circuit, lastSample);
});

/**
 * Build an `AudioFrame` from the engine's transient sample + circuit
 * topology. For each Speaker / Buzzer component, derive its pin
 * voltage and pack the `ComponentAudioState`.
 *
 * Pin voltage for a 2-pin audio component is the difference between
 * the two terminal nets (signed) — matches the resistor branch
 * current adapter in fx-state.extractBranchMeasurements.
 */
export function extractAudioFrame(circuit: Circuit, sample: TransientWireSample): AudioFrame {
  const components = new Map<string, ComponentAudioState>();
  for (const component of circuit.components) {
    const state = buildComponentAudioState(component, sample);
    if (state) components.set(component.id, state);
  }
  return { t: sample.t, components };
}

function buildComponentAudioState(
  component: ComponentInstance,
  sample: TransientWireSample,
): ComponentAudioState | null {
  if (component.type !== "speaker" && component.type !== "buzzer") return null;
  const t0 = component.terminals[0];
  const t1 = component.terminals[1];
  if (!t0 || !t1) return null;
  const V0 = sample.nodeVoltages[t0.netId] ?? 0;
  const V1 = sample.nodeVoltages[t1.netId] ?? 0;
  const V_pin = V0 - V1;

  if (component.type === "speaker") {
    return {
      componentId: component.id,
      type: "speaker",
      frequency: speakerFrequency(V_pin),
      volume: speakerVolume(V_pin),
      active: speakerActive(V_pin),
    };
  }
  // buzzer
  return {
    componentId: component.id,
    type: "buzzer",
    frequency: buzzerFrequency(V_pin),
    volume: buzzerVolume(V_pin),
    active: buzzerActive(V_pin),
  };
}

/** Setter triggers persistence in canvas-prefs.persistThemePrefs (S31-6 wiring). */
export function setAudioMuted(muted: boolean): void {
  audioMuted.value = muted;
  void persistAudioPrefsAsync();
}

export function setAudioMasterVolume(volume: number): void {
  audioMasterVolume.value = Math.max(0, Math.min(1, volume));
  void persistAudioPrefsAsync();
}

export function dismissAutoplayHint(): void {
  audioAutoplayHinted.value = true;
  void persistAudioPrefsAsync();
}

/**
 * Lazy-init the AudioContext + create the Tone.js engine handle on
 * the first user gesture that produces sound. Idempotent.
 *
 * The handle is module-scoped so the rAF loop in vxa-canvas.ts (or a
 * future audio-driven loop) can `update()` it per frame without
 * re-creating Tone nodes.
 */
let engine: AudioEngineHandle | null = null;
export async function ensureAudioEngine(): Promise<AudioEngineHandle | null> {
  if (engine) return engine;
  try {
    await ensureAudioContext();
    if (!isAudioContextReady()) return null;
    const { createToneEngine } = await import("@voltxampere/audio");
    engine = await createToneEngine();
    return engine;
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn("[VXA audio] ensureAudioEngine failed:", err);
    }
    return null;
  }
}

export function destroyAudioEngine(): void {
  if (engine) {
    engine.destroy();
    engine = null;
  }
}

export function getAudioEngine(): AudioEngineHandle | null {
  return engine;
}

async function persistAudioPrefsAsync(): Promise<void> {
  try {
    const mod = await import("./canvas-prefs.js");
    mod.persistThemePrefs();
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn("[VXA audio-state] persist trigger failed:", err);
    }
  }
}

/** Test cleanup. */
export function __resetAudioStateForTests(): void {
  audioMuted.value = false;
  audioMasterVolume.value = 0.5;
  audioAutoplayHinted.value = false;
  documentHidden.value = false;
  visibilityBound = false;
  destroyAudioEngine();
}
